import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;
const EDITORIAL_DESKTOP_MEDIA_QUERY = '(min-width: 992px)';
const EDITORIAL_EXIT_FALLBACK_MS = 520;
const EDITORIAL_SWITCH_FALLBACK_MS = 420;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {MutationObserver | null} */
  #editorialImageObserver = null;

  /** @type {ResizeObserver | null} */
  #editorialLayoutObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {Map<HTMLElement, () => void>} */
  #editorialMotionCleanups = new Map();

  /** @type {Map<HTMLElement, number>} */
  #editorialOpenTimers = new Map();

  /** @type {MediaQueryList} */
  #editorialDesktopMedia = window.matchMedia(EDITORIAL_DESKTOP_MEDIA_QUERY);

  connectedCallback() {
    super.connectedCallback();

    this.#mountEditorialImages();
    this.#editorialImageObserver = new MutationObserver(() => this.#mountEditorialImages());
    this.#editorialImageObserver.observe(this, { childList: true, subtree: true });
    onDocumentLoaded(() => {
      this.#mountEditorialImages();
      this.#syncEditorialLayout();
    });
    this.#syncEditorialLayout();
    const navigation = this.querySelector('.header-menu__inner');
    if (navigation) {
      this.#editorialLayoutObserver = new ResizeObserver(() => this.#syncEditorialLayout());
      this.#editorialLayoutObserver.observe(navigation);
    }
    this.addEventListener('pointerover', this.#onEditorialImagePointerOver);
    document.addEventListener('keydown', this.#onKeyDown);
    window.addEventListener('resize', this.#resizeListener);
    this.#editorialDesktopMedia.addEventListener('change', this.#onEditorialBreakpointChange);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#onKeyDown);
    window.removeEventListener('resize', this.#resizeListener);
    this.#editorialDesktopMedia.removeEventListener('change', this.#onEditorialBreakpointChange);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#editorialImageObserver?.disconnect();
    this.#editorialImageObserver = null;
    this.#editorialLayoutObserver?.disconnect();
    this.#editorialLayoutObserver = null;
    this.removeEventListener('pointerover', this.#onEditorialImagePointerOver);
    this.#resetEditorialMotion();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#syncEditorialLayout();
  }, 100);

  #onEditorialBreakpointChange = () => {
    this.#syncEditorialLayout();

    if (!this.#editorialDesktopMedia.matches) {
      this.#resetEditorialMotion();
    }
  };

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Treat the editorial image as a deliberate dismissal zone.
   * @param {PointerEvent} event
   */
  #onEditorialImagePointerOver = (event) => {
    if (window.Shopify?.designMode || !(event.target instanceof Element) || !this.#state.activeItem) return;

    const imageArea = event.target.closest('.mega-menu__content--editorial');
    if (!imageArea || !this.contains(imageArea)) return;
    if (event.relatedTarget instanceof Node && imageArea.contains(event.relatedTarget)) return;

    const activeSubmenu = findSubmenu(this.#state.activeItem);
    if (!activeSubmenu?.contains(imageArea)) return;

    this.#deactivate();
  };

  /**
   * Close the active submenu and return focus to its top-level link.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    const activeItem = this.#state.activeItem;
    if (event.key !== 'Escape' || !activeItem) return;

    event.preventDefault();
    activeItem.focus({ preventScroll: true });
    this.#deactivate(activeItem);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    const submenuForItem = findSubmenu(item);

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const activeSubmenu = findSubmenu(previouslyActiveItem);
    const transitioningSubmenu = Array.from(
      this.querySelectorAll('.menu-list__submenu--editorial[data-motion-state]'),
    ).find((candidate) => candidate !== submenuForItem);
    const previousSubmenu = activeSubmenu ?? transitioningSubmenu ?? null;
    const isEditorialSwitch =
      this.#isEditorialSubmenu(previousSubmenu) && this.#isEditorialSubmenu(submenuForItem);

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    if (this.#isEditorialSubmenu(previousSubmenu) && previousSubmenu !== submenuForItem) {
      this.#closeEditorialSubmenu(previousSubmenu, { switching: isEditorialSwitch });
    } else if (previousSubmenu && previousSubmenu !== submenuForItem) {
      delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = submenuForItem;
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      if (this.#isEditorialSubmenu(submenu)) {
        this.#syncEditorialLayout();
        this.#openEditorialSubmenu(submenu, { switching: isEditorialSwitch });
      }

      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              if (this.#isEditorialSubmenu(submenu)) {
                this.#setFullOpenHeaderHeight(submenu.offsetHeight, this.#getHeaderVisibleHeight());
              }
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    const hasVisibleEditorialPanel = this.#hasVisibleEditorialPanel();

    if (finalHeight > 0 || !hasVisibleEditorialPanel) {
      this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
      this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    }
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const eventItem = findOwningMenuItem(event.target);
    if (eventItem && eventItem !== this.#state.activeItem) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    const submenu = findSubmenu(item);
    const isEditorialSubmenu = this.#isEditorialSubmenu(submenu);

    if (isEditorialSubmenu) {
      this.#closeEditorialSubmenu(submenu);
    } else {
      this.headerComponent?.style.setProperty('--submenu-height', '0px');
      this.#setFullOpenHeaderHeight(0, 0);
      this.style.setProperty('--submenu-opacity', '0');
    }

    this.dataset.overflowExpanded = 'false';

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu && !isEditorialSubmenu) {
      delete submenu.dataset.active;
    }

    if (isEditorialSubmenu) {
      this.#collapseEditorialUnderlayIfIdle();
    }
  };

  /**
   * Whether an element is a desktop editorial submenu.
   * @param {HTMLElement | null} submenu
   * @returns {submenu is HTMLElement}
   */
  #isEditorialSubmenu(submenu) {
    return (
      this.dataset.menuPresentation === 'editorial' &&
      this.#editorialDesktopMedia.matches &&
      submenu?.classList.contains('menu-list__submenu--editorial') === true
    );
  }

  /**
   * Set the editorial menu height to the visible viewport below the active header row.
   */
  #syncEditorialLayout() {
    if (this.dataset.menuPresentation !== 'editorial' || !this.headerComponent) return;

    if (!this.#editorialDesktopMedia.matches) {
      this.headerComponent.style.removeProperty('--editorial-menu-height');
      this.headerComponent.style.removeProperty('--editorial-menu-link-column-width');
      return;
    }

    const overlapsBottomRow = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = overlapsBottomRow
      ? this.headerComponent.querySelector('.header__row--top')
      : this.headerComponent;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const boundaryBottom = boundary?.getBoundingClientRect().bottom ?? 0;
    const menuHeight = Math.max(0, viewportHeight - boundaryBottom + 1);
    const navigation = this.querySelector('.header-menu__inner');
    const navigationRight = navigation?.getBoundingClientRect().right ?? 0;
    const linkColumnWidth = Math.max(240, Math.min(window.innerWidth, navigationRight));

    this.headerComponent.style.setProperty('--editorial-menu-height', `${menuHeight}px`);
    this.headerComponent.style.setProperty('--editorial-menu-link-column-width', `${linkColumnWidth}px`);

    const activeEditorialSubmenu = this.querySelector(
      '.menu-list__submenu--editorial[data-motion-state][data-motion-front]',
    );
    if (activeEditorialSubmenu instanceof HTMLElement) {
      const submenuHeight = activeEditorialSubmenu.offsetHeight;
      this.headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
      this.#setFullOpenHeaderHeight(submenuHeight, this.#getHeaderVisibleHeight());
    }
  }

  /**
   * Open an editorial submenu from a guaranteed pre-animation state.
   * Reopening during exit reverses naturally from the current rendered position.
   * @param {HTMLElement} submenu
   * @param {{ switching?: boolean }} [options]
   */
  #openEditorialSubmenu(submenu, { switching = false } = {}) {
    this.#cancelEditorialMotion(submenu);
    submenu.inert = false;
    submenu.dataset.active = '';
    submenu.dataset.motionFront = '';
    submenu.dataset.motionState = 'enter-start';

    if (switching) {
      submenu.dataset.motionMode = 'switch';
    } else {
      delete submenu.dataset.motionMode;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      submenu.dataset.motionState = 'open';
      return;
    }

    // Commit the off-canvas state before targeting the open state. This makes
    // every cold open animate even when a previous animation was interrupted.
    submenu.getBoundingClientRect();
    const editorialContent = submenu.querySelector('.mega-menu__content--editorial');
    if (editorialContent) {
      const editorialContentStyle = getComputedStyle(editorialContent);
      void editorialContentStyle.clipPath;
      void editorialContentStyle.transform;
    }
    const openTimer = window.setTimeout(() => {
      this.#editorialOpenTimers.delete(submenu);
      if (submenu.dataset.motionState === 'enter-start') {
        submenu.dataset.motionState = 'open';
      }
    }, 34);
    this.#editorialOpenTimers.set(submenu, openTimer);
  }

  /**
   * Keep an editorial submenu visible until its outbound motion completes.
   * @param {HTMLElement} submenu
   * @param {{ switching?: boolean }} [options]
   */
  #closeEditorialSubmenu(submenu, { switching = false } = {}) {
    this.#cancelEditorialMotion(submenu);
    submenu.inert = true;
    delete submenu.dataset.motionFront;
    submenu.dataset.motionState = switching ? 'switch-out' : 'closing';

    const expectedState = submenu.dataset.motionState;
    const motionTarget = submenu.querySelector('.mega-menu__content--editorial');
    let timeout;

    const cleanup = () => {
      if (timeout) window.clearTimeout(timeout);
      motionTarget?.removeEventListener('transitionend', onTransitionEnd);
    };

    const finish = () => {
      cleanup();
      this.#editorialMotionCleanups.delete(submenu);
      if (submenu.dataset.motionState !== expectedState) return;

      delete submenu.dataset.motionState;
      delete submenu.dataset.motionMode;
      delete submenu.dataset.motionFront;
      delete submenu.dataset.active;
      submenu.inert = false;
      this.#collapseEditorialUnderlayIfIdle();
    };

    const onTransitionEnd = (event) => {
      if (!switching && event.target === motionTarget && event.propertyName === 'transform') {
        finish();
      }
    };

    if (!switching) {
      motionTarget?.addEventListener('transitionend', onTransitionEnd);
    }

    timeout = window.setTimeout(
      finish,
      switching ? EDITORIAL_SWITCH_FALLBACK_MS : EDITORIAL_EXIT_FALLBACK_MS,
    );
    this.#editorialMotionCleanups.set(submenu, cleanup);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
    }
  }

  /**
   * Cancel pending motion work without changing the current rendered state.
   * @param {HTMLElement} submenu
   */
  #cancelEditorialMotion(submenu) {
    const openTimer = this.#editorialOpenTimers.get(submenu);
    if (openTimer !== undefined) {
      window.clearTimeout(openTimer);
      this.#editorialOpenTimers.delete(submenu);
    }

    this.#editorialMotionCleanups.get(submenu)?.();
    this.#editorialMotionCleanups.delete(submenu);
  }

  /** @returns {boolean} */
  #hasVisibleEditorialPanel() {
    return Boolean(this.querySelector('.menu-list__submenu--editorial[data-motion-state]'));
  }

  #collapseEditorialUnderlayIfIdle() {
    if (this.#hasVisibleEditorialPanel()) return;

    const activeSubmenu = findSubmenu(this.#state.activeItem);
    if (this.#isEditorialSubmenu(activeSubmenu)) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
  }

  #resetEditorialMotion() {
    const submenus = this.querySelectorAll('.menu-list__submenu--editorial');
    submenus.forEach((submenu) => {
      if (!(submenu instanceof HTMLElement)) return;
      this.#cancelEditorialMotion(submenu);
      delete submenu.dataset.motionState;
      delete submenu.dataset.motionMode;
      delete submenu.dataset.motionFront;
      delete submenu.dataset.active;
      submenu.inert = false;
    });

    if (this.#state.activeItem) {
      this.#state.activeItem.ariaExpanded = 'false';
      this.#state.activeItem = null;
      this.ariaExpanded = 'false';
    }

    this.#collapseEditorialUnderlayIfIdle();
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Move merchant-configured editorial images into the matching menu panel.
   */
  #mountEditorialImages() {
    const targets = Array.from(this.querySelectorAll('[data-menu-url].mega-menu__editorial-image-link'));
    const imageConfigs = this.querySelectorAll('[data-mega-menu-image][data-has-image]');

    imageConfigs.forEach((imageConfig) => {
      const configuredUrl = normalizeMenuUrl(imageConfig.getAttribute('data-menu-url'));
      if (!configuredUrl) return;

      const target = targets.find(
        (candidate) =>
          !candidate.hasAttribute('data-configured-image') &&
          normalizeMenuUrl(candidate.getAttribute('data-menu-url')) === configuredUrl,
      );
      if (!target) return;

      target.replaceChildren(imageConfig);
      target.setAttribute('data-configured-image', '');
    });
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the top-level menu link that owns an event target.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findOwningMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item');
  const menuItem = listItem?.querySelector('[ref="menuitem"]');
  return menuItem instanceof HTMLElement ? menuItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Normalize menu and theme-setting URLs for reliable matching.
 * @param {string | null} value
 * @returns {string}
 */
function normalizeMenuUrl(value) {
  if (!value) return '';

  const shopifyResource = value.match(/^shopify:\/\/(collections|products|pages)\/(.+)$/);
  if (shopifyResource) {
    const [, resourceType, handle] = shopifyResource;
    return `/${resourceType}/${handle}`.replace(/\/$/, '');
  }

  try {
    const url = new URL(value, window.location.origin);
    return `${url.pathname.replace(/\/$/, '') || '/'}${url.search}`;
  } catch {
    return value.replace(/\/$/, '');
  }
}
